import { createContext, useEffect, useState } from 'react'
import { GlobalStyles } from 'tss-react'
import {
  createTheme,
  Palette,
  PaletteOptions,
  StyledEngineProvider,
  ThemeProvider,
  useColorScheme,
} from '@mui/material/styles'
import { ThemeModes } from '../../enums'

declare module '@mui/material/Checkbox' {
  export interface CheckboxPropsColorOverrides {
    primaryText: true
    secondaryText: true
    greyScale: true
    primaryWhite: true
  }
}
declare module '@mui/material/Fab' {
  export interface FabPropsColorOverrides {
    primaryText: true
    secondaryText: true
    greyScale: true
    primaryWhite: true
  }
}
declare module '@mui/material/Button' {
  export interface ButtonPropsColorOverrides {
    primaryText: true
    secondaryText: true
    greyScale: true
    primaryWhite: true
  }

  export interface ButtonPropsVariantOverrides {
    assetPrimary: true
    assetSecondary: true
  }
}

declare module '@mui/material/IconButton' {
  export interface IconButtonPropsColorOverrides {
    primaryText: true
    secondaryText: true
    greyScale: true
    primaryWhite: true
  }
}

declare module '@mui/material/styles/createPalette' {
  export interface PaletteOptions {
    primaryText: VesselColorToken
    primaryWhite: VesselColorToken
    secondaryText: VesselColorToken
    complementaryBlue: VesselColorToken
    paperOnPaper: VesselColorToken
    activeGreen: VesselColorToken
    alternateRowGrey: VesselColorToken
    calmRed: VesselColorToken
    mainWarning: VesselColorToken
    errorText: VesselColorToken
    textLink: VesselColorToken
    primarySelected: VesselColorToken
    alternateRowsEven: VesselColorToken
    alternateRowsOdd: VesselColorToken
    darkIcon: VesselColorToken
    greyScale: VesselColorToken
    greyPrimary: VesselColorToken
    alertbg: VesselColorToken
    hover: VesselColorToken
    activeButtonBackground: VesselColorToken
  }
}

declare interface VesselColorToken {
  main: string
  light?: string
  dark?: string
  text?: string
  contrastText?: string
  background?: string
  hover?: string
}

declare interface AssetPaletteOptions extends PaletteOptions {
  defaultText?: VesselColorToken
  inverseText?: VesselColorToken
  card?: {
    background?: VesselColorToken
    border?: VesselColorToken
    text?: VesselColorToken
  }
  shadow?: VesselColorToken
  white?: string
  black?: string
  dropdownMenuGroupLabel?: VesselColorToken
  icon?: VesselColorToken
  container?: VesselColorToken
  dropdownMenuGroup?: VesselColorToken
}

declare module '@mui/material/styles' {
  interface Theme {
    themeMode?: ThemeModes
    setTheme?: (...args: any[]) => void
    assetPalette?: Palette & AssetPaletteOptions
  }

  interface ThemeOptions {
    assetPalette?: PaletteOptions & AssetPaletteOptions
  }

  interface BreakpointOverrides {
    xs: true // removes the `xs` breakpoint
    sm: true
    md: true
    lg: true
    xl: true
    xxl: true
  }
}
interface ThemeContextType {
  themeMode: ThemeModes
  setTheme: (mode: ThemeModes) => void
}

export const ThemeContext = createContext<ThemeContextType>({
  themeMode: ThemeModes.Light,
  setTheme: () => {},
})

export const ThemeContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { mode: muiMode, setMode: setMuiMode } = useColorScheme()
  const [mode, setMode] = useState<ThemeModes>(muiMode === 'dark' ? ThemeModes.Dark : ThemeModes.Light)

  useEffect(() => {
    if (muiMode) {
      const newMode = muiMode === 'dark' ? ThemeModes.Dark : ThemeModes.Light
      setMode(newMode)
    }
  }, [muiMode])

  const lightAssetPalette = {
    mode: 'light' as const,
    primary: {
      main: '#00526C',
      dark: '#00304A',
      contrastText: '#F2F2F2',
      light: '#0074B2',
    },
    secondary: {
      main: '#838383',
      dark: '#606060',
      light: '#D9E1E7',
    },
    black: '#000000',
    success: {
      main: '#2E7D32',
      dark: '#1B5E20',
      light: '#4CAF50',
    },
    error: {
      main: '#D32F2F',
      dark: '#C62828',
      light: '#EF5350',
    },
    mainWarning: {
      main: '#FF9800',
      dark: '#E65100',
      light: '#FF9800',
    },
    warning: {
      main: '#ED6C02',
      light: '#FF9800',
    },
    background: {
      main: '#F1F5F7',
      dark: '#303030',
    },
    paperOnPaper: {
      main: '#cbcbcb',
    },
    divider: '#e6e6e6',
    hover: {
      main: '#e8f0fe',
      dark: '#e6e6e6a0',
    },
    primaryText: {
      main: '#00526C',
      dark: '#00304A',
      light: '#0288d1',
      contrastText: '#F2F2F2',
    },
    primaryWhite: {
      main: '#F2F2F2',
      dark: '#F2F2F2',
      light: '#F2F2F2',
    },
    secondaryText: {
      main: '#219A58',
      dark: '#208847',
    },
    complementaryBlue: {
      main: '#00526b36',
      light: '#0cc6ff',
      dark: '#09169e',
      contrastText: '#ffffff',
    },
    activeGreen: {
      main: '#e8f5e9',
      light: '#e8f5e9',
      dark: '#e8f5e9',
    },
    alternateRowGrey: {
      main: '#f9f9f9',
      light: '#f9f9f9',
      dark: '#f9f9f9',
    },
    calmRed: {
      main: '#e57373',
      light: '#f6cfcf',
      dark: '#b5423e',
    },
    errorText: {
      main: '#B40000',
      light: '#BF0000',
      dark: '#A50000',
    },
    textLink: {
      main: '#0C80B9',
      light: '#085B9C',
      dark: '#0C80B9',
    },
    icon: {
      main: '#00526C',
      background: '#E8E8E8',
      hover: '#d0d0d0',
    },

    alternateRowsEven: {
      main: '#e7f5f98b',
      light: '#0C80B9',
      dark: '#3C92A9',
    },
    alternateRowsOdd: {
      main: '#FFFFFF',
      light: '#FFFFFF',
      dark: '#BABABA',
    },
    primarySelected: {
      main: '#EBEFF1',
      dark: '#EBEFF1',
    },
    info: {
      main: '#F1F5F7',
      light: '#F1F5F7',
    },
    darkIcon: {
      main: '#242424',
      light: '#2B2B2B',
      dark: '#000000',
    },
    greyScale: {
      main: '#282828',
      light: '#4C4C4C',
      dark: '#d2d2d2',
      text: '#919090',
    },
    greyPrimary: {
      main: '#eeeeee',
      light: '#4C4C4C',
      dark: '#AFAFAF',
      text: '#919090',
    },
    alertbg: {
      main: '#b5423e',
      light: '#e5841a',
      dark: '#00304A',
      text: '#00304A',
    },
    dropdownMenuGroup: {
      main: '#F0F0F0',
    },
    // Custom asset palette properties
    defaultText: {
      main: '#00526C',
      contrastText: '#ffffff',
    },
    inverseText: {
      main: '#ffffff',
      contrastText: '#00526C',
    },
    card: {
      background: {
        main: '#ffffff',
        dark: '#303030',
      },
      border: {
        main: '#EAEAEA',
      },
      text: {
        main: '#000000',
        dark: '#f5f5f5',
      },
    },
    container: {
      main: '#ffffff',
      dark: '#000000ff',
    },
    shadow: {
      main: '#00000029',
    },
    white: '#FFFFFF',
    activeButtonBackground: {
      main: '#d3edff',
    },
  }

  // Dark mode asset palette
  const darkAssetPalette = {
    mode: 'dark' as const,
    primary: {
      main: '#02799c',
      dark: '#00304A',
      contrastText: '#F2F2F2',
      light: '#0074B2',
    },
    secondary: {
      main: '#02799c',
      dark: '#606060',
      light: '#D9E1E7',
    },
    black: '#000000',
    white: '#FFFFFF',
    success: {
      main: '#2E7D32',
      dark: '#1B5E20',
      light: '#4CAF50',
    },
    error: {
      main: '#b5423e',
      dark: '#C62828',
      light: '#EF5350',
    },
    mainWarning: {
      main: '#f58700',
      dark: '#f58700',
      light: '#FF9800',
    },
    warning: {
      main: '#ED6C02',
      light: '#FF9800',
    },
    background: {
      main: '#303030',
      dark: '#303030',
    },
    paperOnPaper: {
      main: '#cbcbcb',
    },
    divider: '#e6e6e6',
    hover: {
      main: '#e6e6e6a0',
      dark: '#e6e6e6a0',
    },
    primaryText: {
      main: '#02799c',
      dark: '#F5F5F5',
      light: '#46BDDF',
      contrastText: '#F2F2F2',
      text: '#F2F2F2',
    },
    primaryWhite: {
      main: '#F2F2F2',
      dark: '#F2F2F2',
      light: '#F2F2F2',
    },
    secondaryText: {
      main: '#07DF89',
      dark: '#07A15D',
    },
    complementaryBlue: {
      main: '#00526b36',
      light: '#0cc6ff',
      dark: '#09169e',
      contrastText: '#ffffff',
    },
    activeGreen: {
      main: '#e8f5e9',
      light: '#e8f5e9',
      dark: '#e8f5e9',
    },
    alternateRowGrey: {
      main: '#f9f9f9',
      light: '#f9f9f9',
      dark: '#f9f9f9',
    },
    calmRed: {
      main: '#e57373',
      light: '#f6cfcf',
      dark: '#b5423e',
    },
    info: {
      main: '#37474f',
      light: '#62727b',
    },
    errorText: {
      main: '#ED506B',
      light: '#ED506B',
      dark: '#ED506B',
    },
    textLink: {
      main: '#0C80B9',
      light: '#1Ca2dC',
      dark: '#0C80B9',
    },
    icon: {
      main: '#ffffff',
      background: '#333333',
      hover: '#555555',
    },
    dropdownMenuGroup: {
      main: '#F0F0F0',
    },
    alternateRowsEven: {
      main: '#1E1E1E',
      light: '#0C80B9',
      dark: '#3C92A9',
    },
    alternateRowsOdd: {
      main: '#303030',
      light: '#FFFFFF',
      dark: '#303030',
    },
    primarySelected: {
      main: '#3C474B',
      dark: '#303C40',
    },
    darkIcon: {
      main: '#D4D4D4',
      light: '#ffffff',
      dark: '#AFAFAF',
    },
    greyScale: {
      main: '#cbcbcb',
      light: '#C4C4C4',
      dark: '#AFAFAF',
      text: '#919090',
    },
    greyPrimary: {
      main: '#00304A',
      light: '#4C4C4C',
      dark: '#d5d5d5',
      text: '#b7b7b7',
    },
    alertbg: {
      main: '#b5423e',
      light: '#e5841a',
      dark: '#00304A',
      text: '#00304A',
    },
    // Custom asset palette properties
    defaultText: {
      main: '#ffffff',
      contrastText: '#00526C',
    },
    inverseText: {
      main: '#00526C',
      contrastText: '#ffffff',
    },
    card: {
      background: {
        main: '#2d2d2d',
        dark: '#1a1a1a',
      },
      border: {
        main: '#444444',
      },
      text: {
        main: '#f5f5f5',
        dark: '#ffffff',
      },
    },
    container: {
      main: '#2d2d2d',
      dark: '#1a1a1a',
    },
    shadow: {
      main: '#00000050',
    },
    activeButtonBackground: {
      main: '#234dc2',
    },
  }

  // @charith, you gotta tell me what this is, now we just have double the amount of colors to maintain
  const theme = createTheme({
    assetPalette: mode === ThemeModes.Dark ? darkAssetPalette : lightAssetPalette,
    colorSchemes: {
      dark: {
        palette: {
          mode: 'dark',
          AppBar: {
            darkBg: '#00314A',
            // color: '#ffffff',
          },
          primary: {
            main: '#00314A',
            dark: '#00314A',
            light: '#02799c',
            contrastText: '#ffffff',
          },
          primaryWhite: {
            main: '#F2F2F2',
            dark: '#F2F2F2',
            light: '#F2F2F2',
          },
          primaryText: {
            main: '#02799c',
            dark: '#F5F5F5',
            light: '#46BDDF',
            contrastText: '#F2F2F2',
            text: '#F2F2F2',
          },
          primarySelected: {
            main: '#3C474B',
            dark: '#303C40',
          },
          secondary: {
            main: '#02799c',
            light: '#02799c',
            dark: '#00304A',
          },
          secondaryText: {
            main: '#07DF89',
            dark: '#07A15D',
          },
          divider: '#e6e6e6',
          error: {
            main: '#b5423e',
            dark: '#cc3333',
          },
          mainWarning: {
            main: '#b76603',
            light: '#c96715',
            dark: '#f58700',
          },
          background: {
            main: '#303030',
            // light: '#303030',
            dark: '#292929',
            // paper: '#0D0D0D',
          },
          hover: {
            main: '#e6e6e6a0',
            light: '#e6e6e6a0',
            dark: '#e6e6e6a0',
          },
          paperOnPaper: {
            main: '#0D0D0D',
            dark: '#0D0D0D',
            light: '#2C2C2C',
          },
          complementaryBlue: {
            main: '#00526b36',
            light: '#0cc6ff',
            dark: '#09169e',
            contrastText: '#ffffff',
          },
          activeGreen: {
            main: '#e8f5e9',
            light: '#e8f5e9',
            dark: '#e8f5e9',
          },
          alternateRowGrey: {
            main: '#f9f9f9',
            light: '#f9f9f9',
            dark: '#f9f9f9',
          },
          calmRed: {
            main: '#e57373',
            light: '#f6cfcf',
            dark: '#b5423e',
          },
          errorText: {
            main: '#ED506B',
            light: '#ED506B',
            dark: '#ED506B',
          },
          textLink: {
            main: '#0C80B9',
            light: '#1Ca2dC',
            dark: '#0C80B9',
          },
          alternateRowsEven: {
            main: '#1E1E1E',
            light: '#0C80B9',
            dark: '#3C92A9',
          },
          alternateRowsOdd: {
            main: '#303030',
            light: '#FFFFFF',
            dark: '#303030',
          },
          darkIcon: {
            main: '#D4D4D4',
            light: '#ffffff',
            dark: '#AFAFAF',
          },
          greyScale: {
            main: '#cbcbcb',
            light: '#C4C4C4',
            dark: '#AFAFAF',
            text: '#919090',
          },
          greyPrimary: {
            main: '#00304A',
            light: '#4C4C4C', // unused
            dark: '#d5d5d5', // unused
            text: '#b7b7b7',
          },
          alertbg: {
            main: '#b5423e',
            light: '#e5841a', // unused
            dark: '#00304A', // unused
            text: '#00304A',
          },
          activeButtonBackground: {
            main: '#234dc2',
          },
        },
      },
      light: {
        palette: {
          mode: 'light',
          AppBar: {
            darkBg: '#00314A',
            // color: '#ffffff',
          },
          primary: {
            main: '#00304A',
            dark: '#00304A',
            light: '#02799c',
            contrastText: '#ffffff',
          },
          primaryWhite: {
            main: '#00304A',
            dark: '#00304A',
            light: '#00304A',
          },
          primaryText: {
            main: '#00304A',
            dark: '#00314A',
            // light: '#045A80',
            light: '#00314A',
            contrastText: '#F2F2F2',
            text: '#00304A',
          },
          primarySelected: {
            main: '#EBEFF1',
            dark: '#EBEFF1',
          },
          secondary: {
            main: '#21B573',
            light: '#21B573',
            dark: '#21AA68',
          },
          secondaryText: {
            main: '#219A58',
            dark: '#208847',
          },
          error: {
            main: '#b5423e',
            dark: '#b71c1c',
          },
          mainWarning: {
            main: '#f58700',
            light: '#f1b778',
            dark: '#F18932',
          },
          background: {
            main: '#eee',
            dark: '#dfdfdf',
          },
          paperOnPaper: {
            main: '#cbcbcb',
            dark: '#A0A0A0',
            light: '#E4E4E4',
          },
          divider: '#e6e6e6',
          hover: {
            main: '#e8f0fe',
            dark: '#e6e6e6a0',
            light: '#e6e6e6a0',
          },
          complementaryBlue: {
            main: '#00526b36',
            light: '#0cc6ff',
            dark: '#09169e',
            contrastText: '#ffffff',
          },
          activeGreen: {
            main: '#e8f5e9',
            light: '#e8f5e9',
            dark: '#e8f5e9',
          },
          alternateRowGrey: {
            main: '#f9f9f9',
            light: '#f9f9f9',
            dark: '#f9f9f9',
          },
          calmRed: {
            main: '#e57373',
            light: '#f6cfcf',
            dark: '#b5423e',
          },
          errorText: {
            main: '#B40000',
            light: '#BF0000',
            dark: '#A50000',
          },
          textLink: {
            main: '#0C80B9',
            light: '#085B9C',
            dark: '#0C80B9',
          },
          alternateRowsEven: {
            main: '#e7f5f98b',
            light: '#0C80B9',
            dark: '#3C92A9',
          },
          alternateRowsOdd: {
            main: '#FFFFFF',
            light: '#FFFFFF',
            dark: '#BABABA',
          },
          darkIcon: {
            main: '#242424',
            light: '#2B2B2B',
            dark: '#000000',
          },
          greyScale: {
            main: '#282828',
            light: '#4C4C4C',
            dark: '#d2d2d2', // unused
            text: '#919090',
          },
          greyPrimary: {
            main: '#eeeeee',
            light: '#4C4C4C', // unused
            dark: '#AFAFAF', // unused
            text: '#919090',
          },
          alertbg: {
            main: '#b5423e',
            light: '#e5841a', // unused
            dark: '#00304A', // unused
            text: '#00304A',
          },
          activeButtonBackground: {
            main: '#d3edff',
          },
        },
      },
    },
    typography: {
      fontFamily: ['Roboto', 'Helvetica', 'Arial', 'sans-serif'].join(','),
      caption: {
        color: '#5f6368',
        fontSize: 14,
        fontWeight: 500,
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
        xxl: 2400,
      },
    },
  })

  theme.components = {
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(120, 120, 120, 0.12)',
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: 14,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
        },
        contained: {
          boxShadow: '0px 10px 20px -6px rgb(0 0 0 / 12%)',
        },
      },
      variants: [
        {
          props: { variant: 'assetPrimary' },
          style: {
            backgroundColor: theme.assetPalette?.primary.main,
            color: theme.assetPalette?.primary.contrastText,
            '&:hover': {
              backgroundColor: theme.assetPalette?.primary.dark,
            },
          },
        },
        {
          props: { variant: 'assetSecondary' },
          style: {
            backgroundColor: theme.assetPalette?.secondary.main,
            color: '#ffffff',
            '&:hover': {
              backgroundColor: theme.assetPalette?.secondary.dark,
            },
          },
        },
      ],
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        },
        indicator: {
          backgroundColor: theme.palette.secondary.main,
          height: '4px',
          marginBottom: 2,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: 'white !important',
        },
      },
    },
  }

  const themeContextValue = {
    themeMode: mode,
    setTheme: (newMode: ThemeModes) => {
      setMode(newMode)
      if (setMuiMode) {
        setMuiMode(newMode === ThemeModes.Dark ? 'dark' : 'light')
      }
      if (typeof window !== 'undefined') {
        localStorage.setItem('mui-mode', newMode === ThemeModes.Dark ? 'dark' : 'light')
      }
    },
  }

  return (
    <ThemeContext.Provider value={themeContextValue}>
      <GlobalStyles
        styles={{
          body: {
            backgroundColor: `${theme?.palette?.background?.main ?? '#eee'} !important`,
            height: '100%',
            minHeight: 'calc(100vh - 160px)',
          },
          '.asset-theme': {
            backgroundColor: theme.assetPalette?.background?.main,
            color: theme.assetPalette?.defaultText?.main,
          },
          '.asset-card': {
            backgroundColor: theme.assetPalette?.card?.background?.main,
            border: `1px solid ${theme.assetPalette?.card?.border?.main}`,
            color: theme.assetPalette?.card?.text?.main,
            boxShadow: `0px 3px 6px ${theme.assetPalette?.shadow?.main}`,
          },
        }}
      />
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme} defaultMode={mode === ThemeModes.Dark ? 'dark' : 'light'}>
          {children}
        </ThemeProvider>
      </StyledEngineProvider>
    </ThemeContext.Provider>
  )
}
